/***
 *
 *   USERS / DETAIL
 *   View and manage an individual user
 *
 **********/
import { useContext, useEffect, useState, useRef, useCallback } from 'react';
import Axios from 'axios';
import {
  Animate, Card, Breadcrumbs, Chart, Table, Paginate,
  Select, Icon, Badge, useAPI, ViewContext,
} from 'components/lib';
import { AddCreditsModal } from 'components/addCreditsModal/addCreditsModal';
import { useNavigate, useParams } from 'react-router-dom';
import Settings from 'settings.json';
import { convertToLocalTime, generateStrongPassword } from 'utility/helper';

// ─── Date Range Helpers ─────────────────────────────────────────────────────

function formatDate(d) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function getDateRange(preset) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  switch (preset) {
    case 'last_7': {
      const start = new Date(today);
      start.setDate(start.getDate() - 6);
      return { start: formatDate(start), end: formatDate(today), label: 'Last 7 days' };
    }
    case 'last_30': {
      const start = new Date(today);
      start.setDate(start.getDate() - 29);
      return { start: formatDate(start), end: formatDate(today), label: 'Last 30 days' };
    }
    case 'this_week': {
      const day = today.getDay();
      const start = new Date(today);
      start.setDate(start.getDate() - (day === 0 ? 6 : day - 1));
      return { start: formatDate(start), end: formatDate(today), label: 'This week' };
    }
    case 'last_week': {
      const day = today.getDay();
      const thisMonday = new Date(today);
      thisMonday.setDate(thisMonday.getDate() - (day === 0 ? 6 : day - 1));
      const lastMonday = new Date(thisMonday);
      lastMonday.setDate(lastMonday.getDate() - 7);
      const lastSunday = new Date(thisMonday);
      lastSunday.setDate(lastSunday.getDate() - 1);
      return { start: formatDate(lastMonday), end: formatDate(lastSunday), label: 'Last week' };
    }
    case 'this_month': {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      return { start: formatDate(start), end: formatDate(today), label: 'This month' };
    }
    case 'last_month': {
      const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const end = new Date(today.getFullYear(), today.getMonth(), 0);
      return { start: formatDate(start), end: formatDate(end), label: 'Last month' };
    }
    default:
      return null;
  }
}

const DATE_PRESETS = [
  { key: 'last_7',     label: 'Last 7 days' },
  { key: 'last_30',    label: 'Last 30 days' },
  { key: 'this_week',  label: 'This week' },
  { key: 'last_week',  label: 'Last week' },
  { key: 'this_month', label: 'This month' },
  { key: 'last_month', label: 'Last month' },
];

function DateRangeSelector({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const ref = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const selectPreset = useCallback((key) => {
    const range = getDateRange(key);
    if (range) {
      onChange({ start: range.start, end: range.end, label: range.label, preset: key });
      setOpen(false);
    }
  }, [onChange]);

  const applyCustom = useCallback(() => {
    if (customFrom && customTo) {
      onChange({ start: customFrom, end: customTo, label: `${customFrom} — ${customTo}`, preset: 'custom' });
      setOpen(false);
    }
  }, [customFrom, customTo, onChange]);

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          background: 'none', border: '1px solid #e5e7eb', borderRadius: 6,
          padding: '5px 12px', fontSize: 13, color: '#374151', cursor: 'pointer',
          fontWeight: 500, whiteSpace: 'nowrap',
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
          <line x1="16" y1="2" x2="16" y2="6"/>
          <line x1="8" y1="2" x2="8" y2="6"/>
          <line x1="3" y1="10" x2="21" y2="10"/>
        </svg>
        {value.label}
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>

      {open && (
        <div style={{
          position: 'absolute', right: 0, top: '100%', marginTop: 4,
          background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8,
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)', zIndex: 50,
          width: 240, overflow: 'hidden',
        }}>
          {DATE_PRESETS.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => selectPreset(key)}
              style={{
                display: 'block', width: '100%', textAlign: 'left',
                padding: '9px 14px', border: 'none', background: value.preset === key ? '#f3f4f6' : '#fff',
                fontSize: 13, color: '#374151', cursor: 'pointer',
                fontWeight: value.preset === key ? 600 : 400,
              }}
              onMouseEnter={e => { if (value.preset !== key) e.currentTarget.style.background = '#f9fafb'; }}
              onMouseLeave={e => { if (value.preset !== key) e.currentTarget.style.background = '#fff'; }}
            >
              {label}
            </button>
          ))}

          <div style={{ borderTop: '1px solid #f3f4f6', padding: '10px 14px' }}>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8, fontWeight: 500 }}>Custom range</div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <input
                type="date"
                value={customFrom}
                onChange={e => setCustomFrom(e.target.value)}
                style={{
                  flex: 1, padding: '5px 6px', border: '1px solid #e5e7eb', borderRadius: 4,
                  fontSize: 12, color: '#374151', outline: 'none', minWidth: 0,
                }}
              />
              <input
                type="date"
                value={customTo}
                onChange={e => setCustomTo(e.target.value)}
                style={{
                  flex: 1, padding: '5px 6px', border: '1px solid #e5e7eb', borderRadius: 4,
                  fontSize: 12, color: '#374151', outline: 'none', minWidth: 0,
                }}
              />
            </div>
            <button
              onClick={applyCustom}
              disabled={!customFrom || !customTo}
              style={{
                width: '100%', padding: '6px 0', border: 'none', borderRadius: 4,
                background: customFrom && customTo ? '#2563eb' : '#e5e7eb',
                color: customFrom && customTo ? '#fff' : '#9ca3af',
                fontSize: 12, fontWeight: 500, cursor: customFrom && customTo ? 'pointer' : 'default',
              }}
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const TABS = ['Profile', 'Usage', 'Credits', 'Activity Timeline', 'Billing and Subscription', 'Support History'];

const TAB_SLUGS = {
  'Profile':                   'profile',
  'Usage':                     'usage',
  'Credits':                   'credits',
  'Activity Timeline':         'activity',
  'Billing and Subscription':  'billing',
  'Support History':           'support',
};
const SLUG_TO_TAB = Object.fromEntries(Object.entries(TAB_SLUGS).map(([k, v]) => [v, k]));

const PRICING_MIGRATION_EVENT = 'pricing_migration_stamped';
const PRICING_MIGRATION_LABEL = 'Pricing Migration';

// pricing_migration_stamped is intentionally excluded — the dedicated
// PricingMigration card is its single display surface (it also counts
// dry-run stamps, which would misrepresent live adoption here).
const FEATURE_KEYS = [
  { key: 'document_upload_submitted',  label: 'Doc Upload' },
  { key: 'speech_to_text_used',        label: 'Voice to Text' },
  { key: 'email_ai_submitted',         label: 'Email AI' },
  { key: 'lc_search',                  label: 'LC Search' },
];

const STATUS_COLORS = {
  Success: { bg: '#dcfce7', text: '#16a34a' },
  Info:    { bg: '#dbeafe', text: '#2563eb' },
  Error:   { bg: '#fee2e2', text: '#dc2626' },
  Warning: { bg: '#fef3c7', text: '#d97706' },
};

// ─── Helpers ────────────────────────────────────────────────────────────────

function splitDateTime(str) {
  if (!str) return { date: '—', time: '' };
  const s = String(str);
  const idx = s.indexOf(' ');
  if (idx === -1) return { date: s, time: '' };
  return { date: s.substring(0, idx), time: s.substring(idx + 1) };
}

// Best-effort JSON.parse: returns `fallback` when `raw` isn't valid JSON.
function safeJsonParse(raw, fallback) {
  try { return JSON.parse(raw); } catch { return fallback; }
}

function parseLicenses(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.map(String);
  if (typeof raw === 'string') return safeJsonParse(raw, [raw]);
  return [];
}

// Malformed metadata must not crash the Pricing Migration card — a row we
// can't parse is treated as absent rather than thrown.
function parseMigrationMetadata(raw) {
  if (!raw) return null;
  if (typeof raw === 'object') return raw;
  return safeJsonParse(raw, null);
}

// Friendly display name for raw event names in the Events tab — the
// underlying event/filter value stays the raw name.
export function eventDisplayName(name) {
  return name === PRICING_MIGRATION_EVENT ? PRICING_MIGRATION_LABEL : name;
}

// Events tab free-text search predicate. Matches against the raw name
// (so existing raw-name searches keep working) AND the display name
// (so a search for what the table actually shows, e.g. "pricing
// migration", also matches), plus user_email.
export function eventMatchesSearch(event, term) {
  if (!term) return true;
  const q = term.toLowerCase();
  return (event.name || '').toLowerCase().includes(q) ||
         (event.user_email || '').toLowerCase().includes(q) ||
         (eventDisplayName(event.name) || '').toLowerCase().includes(q);
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function Pill({ bg, color, style, children }) {
  return (
    <span style={{
      fontSize: 12, fontWeight: 500, borderRadius: 6,
      background: bg, color, ...style,
    }}>
      {children}
    </span>
  );
}

function InfoField({ icon, label, children }) {
  return (
    <div style={{ padding: '16px 16px 20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
        <Icon image={icon} size={14} color='#9ca3af' />
        <span style={{ fontSize: 13, color: '#6b7280' }}>{label}</span>
      </div>
      <div style={{ fontSize: 15, color: '#111827', fontWeight: 500, wordBreak: 'break-all' }}>
        {children}
      </div>
    </div>
  );
}

function DateTimeField({ icon, label, value }) {
  const { date, time } = splitDateTime(value);
  return (
    <div style={{ padding: '16px 16px 20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
        <Icon image={icon} size={14} color='#9ca3af' />
        <span style={{ fontSize: 13, color: '#6b7280' }}>{label}</span>
      </div>
      <div style={{ fontSize: 15, fontWeight: 500 }}>
        <span style={{ color: '#111827' }}>{date}</span>
        {time && <span style={{ color: '#9ca3af', marginLeft: 10, fontWeight: 400 }}>{time}</span>}
      </div>
    </div>
  );
}

function InfoRow({ children, cols, noBorder }) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: `repeat(${cols || 3}, 1fr)`,
      borderBottom: noBorder ? 'none' : '1px solid #f3f4f6',
    }}>
      {children}
    </div>
  );
}

function AccountMeta({ account }) {
  const a = account?.data?.[0];

  const rows = [
    { label: 'Organization ID', value: a?.id ? `ORG-${a.id.slice(0, 4).toUpperCase()}` : '—' },
    { label: 'Active Users',    value: a?.active_users != null ? a.active_users : (a?.active !== undefined ? (a.active ? 'Yes' : 'No') : '—') },
    { label: 'Primary Contact', value: a?.email || '—' },
    { label: 'Region',          value: a?.region || a?.country || '—' },
  ];

  return (
    <div>
      <div style={{ fontSize: 15, fontWeight: 600, color: '#111827', marginBottom: 20 }}>Account Metadata</div>
      {rows.map(row => (
        <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14, fontSize: 14 }}>
          <span style={{ color: '#6b7280' }}>{row.label}</span>
          <span style={{ color: '#111827', fontWeight: 500 }}>{row.value}</span>
        </div>
      ))}
    </div>
  );
}

export function FeatureAdoption({ userId }) {
  const features = useAPI(`/api/event?group=name&user_id=${userId}`);

  const counts = {};
  features.data?.results?.forEach(r => {
    counts[r.name] = parseInt(r.total_triggers) || 0;
  });

  return (
    <div>
      <div style={{ fontSize: 15, fontWeight: 600, color: '#111827', marginBottom: 20 }}>Feature Adoption</div>
      {FEATURE_KEYS.map(({ key, label }) => {
        const count = counts[key] || 0;
        const dot = count === 0 && key === 'speech_to_text_used' ? '#f97316' : '#9ca3af';
        return (
          <div key={key} style={{ display: 'flex', alignItems: 'center', marginBottom: 14, fontSize: 14 }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: dot, marginRight: 10, flexShrink: 0 }} />
            <span style={{ color: '#374151', flex: 1 }}>{label}</span>
            <span style={{ color: '#111827', fontWeight: 500 }}>{count.toLocaleString()}</span>
          </div>
        );
      })}
    </div>
  );
}

export function PricingMigration({ userId }) {
  const [showPreview, setShowPreview] = useState(false);
  const migrations = useAPI(`/api/event?user_id=${userId}&name=${PRICING_MIGRATION_EVENT}`);

  const rows = (migrations.data?.list?.results || [])
    .map(row => ({ ...row, meta: parseMigrationMetadata(row.metadata) }))
    .filter(row => row.meta);

  const liveRows    = rows.filter(row => !row.meta.dry_run);
  const previewRows = rows.filter(row => row.meta.dry_run);
  const visibleRows = showPreview ? [...liveRows, ...previewRows] : liveRows;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: '#111827' }}>{PRICING_MIGRATION_LABEL}</div>
        {previewRows.length > 0 && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#6b7280', cursor: 'pointer' }}>
            <input
              type='checkbox'
              checked={showPreview}
              onChange={e => setShowPreview(e.target.checked)}
            />
            Show dry-run previews
          </label>
        )}
      </div>

      {migrations.loading ? (
        <p style={{ color: '#9ca3af', fontSize: 14 }}>Loading…</p>
      ) : visibleRows.length === 0 ? (
        previewRows.length > 0 && !showPreview ? (
          <p style={{ color: '#9ca3af', fontSize: 14 }}>
            No live migration. {previewRows.length} dry-run preview{previewRows.length === 1 ? '' : 's'} hidden.
          </p>
        ) : (
          <p style={{ color: '#9ca3af', fontSize: 14 }}>No pricing migration recorded for this user.</p>
        )
      ) : (
        visibleRows.map((row, i) => {
          const { meta } = row;
          return (
            <div
              key={row.id ?? i}
              style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 14, fontSize: 14 }}
            >
              <div>
                <div style={{ color: '#111827', fontWeight: 500 }}>
                  {meta.before?.plan || '—'}
                  {meta.before?.pa_count != null && ` (${meta.before.pa_count} PA)`}
                  {' → '}
                  {meta.after?.tier || '—'}
                </div>
                <div style={{ color: '#9ca3af', fontSize: 12, marginTop: 2 }}>
                  {convertToLocalTime(row.time)} · {meta.mapping_rule || '—'}
                </div>
              </div>
              {meta.dry_run && (
                <Pill bg='#fef3c7' color='#d97706' style={{ padding: '3px 10px', flexShrink: 0 }}>
                  preview
                </Pill>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}

function ActivityTimeline({ loginHistory }) {
  const items = (loginHistory?.data || []).map(row => ({
    description: [row.device, row.browser].filter(Boolean).join(' · ') || 'User logged in from new device',
    ip: row.ip || '',
    time: convertToLocalTime(row.time),
    status: 'Info',
  }));

  if (loginHistory.loading) return <p style={{ color: '#9ca3af', fontSize: 14 }}>Loading…</p>;
  if (!items.length)         return <p style={{ color: '#9ca3af', fontSize: 14 }}>No activity found.</p>;

  return (
    <div style={{ padding: '4px 0' }}>
      {items.map((item, i) => {
        const sc = STATUS_COLORS[item.status] || STATUS_COLORS.Info;
        return (
          <div
            key={i}
            style={{
              display: 'flex', alignItems: 'flex-start',
              marginBottom: 0, padding: '20px 0',
              borderBottom: i < items.length - 1 ? '1px solid #f3f4f6' : 'none',
            }}
          >
            <div style={{
              width: 10, height: 10, borderRadius: '50%',
              background: sc.text, marginTop: 4, marginRight: 14, flexShrink: 0,
            }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, color: '#111827', fontWeight: 500 }}>{item.description}</div>
              {item.ip && <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>IP: {item.ip}</div>}
              <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>{item.time}</div>
            </div>
            <Pill bg={sc.bg} color={sc.text} style={{ padding: '3px 12px', flexShrink: 0 }}>
              {item.status}
            </Pill>
          </div>
        );
      })}
    </div>
  );
}

function InvoiceStatusBadge({ status }) {
  const isPaid   = String(status || '').toLowerCase() === 'paid';
  const isFailed = String(status || '').toLowerCase() === 'failed';
  const bg    = isPaid ? '#dcfce7' : isFailed ? '#fee2e2' : '#f3f4f6';
  const color = isPaid ? '#16a34a' : isFailed ? '#dc2626' : '#6b7280';
  return (
    <Pill bg={bg} color={color} style={{ padding: '3px 10px' }}>
      {status || '—'}
    </Pill>
  );
}

function BillingTab({ billing }) {
  const [invoiceSearch, setInvoiceSearch] = useState('');
  const [invoicePage,   setInvoicePage]   = useState(1);
  const INVOICE_PAGE_SIZE = 10;

  if (billing.loading) return null;

  if (billing?.data === null) {
    return <p style={{ color: '#9ca3af', fontSize: 14 }}>No active subscription.</p>;
  }

  if (!billing?.data) return null;

  const b = billing.data;

  const planRows = [
    { label: 'Plan Type',     value: b.plan     || '—' },
    { label: 'Monthly Cost',  value: b.amount   || '—' },
    { label: 'Billing Cycle', value: b.interval || '—' },
    { label: 'Next Renewal',  value: b.current_period_end ? convertToLocalTime(b.current_period_end) : '—' },
  ];

  const invoices = b.invoices || [];
  const filteredInvoices = invoiceSearch.trim()
    ? invoices.filter(inv =>
        (inv.id || '').toLowerCase().includes(invoiceSearch.toLowerCase()) ||
        (inv.status || '').toLowerCase().includes(invoiceSearch.toLowerCase())
      )
    : invoices;
  const totalInvoicePages = Math.max(1, Math.ceil(filteredInvoices.length / INVOICE_PAGE_SIZE));
  const pagedInvoices = filteredInvoices.slice(
    (invoicePage - 1) * INVOICE_PAGE_SIZE,
    invoicePage * INVOICE_PAGE_SIZE
  );

  const pageBtnStyle = (disabled) => ({
    background: 'none', border: '1px solid #e5e7eb', borderRadius: 6,
    padding: '4px 8px', cursor: disabled ? 'default' : 'pointer',
    color: disabled ? '#d1d5db' : '#374151', fontSize: 14,
  });

  return (
    <div>
      {/* Top section: Current Plan + Payment Method */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 48, marginBottom: 36 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#111827', marginBottom: 20 }}>Current Plan</div>
          {planRows.map(row => (
            <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, fontSize: 14 }}>
              <span style={{ color: '#6b7280' }}>{row.label}</span>
              <span style={{ color: '#111827', fontWeight: 500 }}>{row.value}</span>
            </div>
          ))}
        </div>
        <div>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#111827', marginBottom: 20 }}>Payment Method</div>
          {b.payment_method ? (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: 16, border: '1px solid #e5e7eb', borderRadius: 8,
              fontSize: 14,
            }}>
              <Icon image='credit-card' size={16} color='#6b7280' />
              <div>
                <div style={{ color: '#111827', fontWeight: 500 }}>
                  •••• •••• •••• {b.payment_method.last4 || '****'}
                </div>
                <div style={{ color: '#9ca3af', fontSize: 12, marginTop: 2 }}>
                  Expires {b.payment_method.exp_month}/{b.payment_method.exp_year}
                </div>
              </div>
            </div>
          ) : (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: 16, border: '1px solid #e5e7eb', borderRadius: 8,
              fontSize: 14,
            }}>
              <Icon image='credit-card' size={16} color='#6b7280' />
              <div>
                <div style={{ color: '#111827', fontWeight: 500 }}>{b.latest_invoice_amount || '—'}</div>
                <div style={{ color: '#9ca3af', fontSize: 12, marginTop: 2 }}>Status: {b.status || '—'}</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Recent Invoices */}
      <div>
        <div style={{ fontSize: 15, fontWeight: 600, color: '#111827', marginBottom: 16 }}>Recent Invoices</div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ position: 'relative', width: 320 }}>
            <span style={{
              position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
              color: '#9ca3af', pointerEvents: 'none', display: 'flex',
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/>
                <line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
            </span>
            <input
              type="text"
              placeholder="Search by name or email..."
              value={invoiceSearch}
              onChange={e => { setInvoiceSearch(e.target.value); setInvoicePage(1); }}
              style={{
                width: '100%', paddingLeft: 38, paddingRight: 12, height: 37,
                border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14,
                color: '#374151', outline: 'none', boxSizing: 'border-box',
              }}
            />
          </div>
          <button style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '8px 16px', border: '1px solid #e5e7eb', borderRadius: 8,
            background: '#fff', fontSize: 14, color: '#374151', cursor: 'pointer',
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="4" y1="6" x2="20" y2="6"/>
              <line x1="8" y1="12" x2="16" y2="12"/>
              <line x1="11" y1="18" x2="13" y2="18"/>
            </svg>
            Filters
          </button>
        </div>

        <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
                {[
                  { label: 'Invoice ID',  sortable: false },
                  { label: 'Date',        sortable: true  },
                  { label: 'Amount',      sortable: true  },
                  { label: 'Status',      sortable: false },
                ].map(col => (
                  <th key={col.label} style={{
                    padding: '12px 16px', textAlign: 'left', fontWeight: 600,
                    fontSize: 13, color: '#6b7280', whiteSpace: 'nowrap',
                    background: '#fff',
                  }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      {col.label}
                      {col.sortable && <span style={{ color: '#d1d5db', fontSize: 11 }}>↕</span>}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pagedInvoices.length === 0 ? (
                <tr>
                  <td colSpan={4} style={{ padding: 32, textAlign: 'center', color: '#9ca3af' }}>
                    No invoices found.
                  </td>
                </tr>
              ) : pagedInvoices.map((inv, i) => {
                const { date, time } = splitDateTime(convertToLocalTime(inv.date || inv.created));
                return (
                  <tr
                    key={i}
                    style={{ borderBottom: '1px solid #f9fafb' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#fafafa'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}
                  >
                    <td style={{ padding: '12px 16px', color: '#374151' }}>{inv.id || '—'}</td>
                    <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                      <span style={{ color: '#111827' }}>{date}</span>
                      {time && (
                        <><br /><span style={{ color: '#9ca3af', fontSize: 12 }}>{time}</span></>
                      )}
                    </td>
                    <td style={{ padding: '12px 16px', color: '#374151' }}>
                      {inv.amount || inv.amount_paid || '—'}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <InvoiceStatusBadge status={inv.status} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {filteredInvoices.length > INVOICE_PAGE_SIZE && (
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '12px 0', marginTop: 4,
          }}>
            <div style={{ fontSize: 14, color: '#6b7280' }}>
              Rows per page <strong style={{ color: '#374151' }}>50</strong>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, color: '#6b7280' }}>
              <span>Page {invoicePage} of {totalInvoicePages}</span>
              <button onClick={() => setInvoicePage(1)}                    disabled={invoicePage === 1}                  style={pageBtnStyle(invoicePage === 1)}>«</button>
              <button onClick={() => setInvoicePage(p => p - 1)}           disabled={invoicePage === 1}                  style={pageBtnStyle(invoicePage === 1)}>‹</button>
              <button onClick={() => setInvoicePage(p => p + 1)}           disabled={invoicePage === totalInvoicePages}  style={pageBtnStyle(invoicePage === totalInvoicePages)}>›</button>
              <button onClick={() => setInvoicePage(totalInvoicePages)}    disabled={invoicePage === totalInvoicePages}  style={pageBtnStyle(invoicePage === totalInvoicePages)}>»</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SupportPriorityBadge({ priority }) {
  const p = String(priority || '').toLowerCase();
  let bg, color, border;
  if (p === 'high') {
    bg = '#fee2e2'; color = '#dc2626'; border = '1px solid #fca5a5';
  } else if (p === 'medium') {
    bg = '#fef9c3'; color = '#ca8a04'; border = '1px solid #fde68a';
  } else {
    bg = '#f3f4f6'; color = '#6b7280'; border = '1px solid #e5e7eb';
  }
  const iconEl = p === 'high'
    ? <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="12" r="10"/></svg>
    : <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/></svg>;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      background: bg, color, border, borderRadius: 999,
      padding: '2px 10px', fontSize: 12, fontWeight: 500,
    }}>
      {iconEl}
      {priority || 'Low'}
    </span>
  );
}

function SupportStatusBadge({ status }) {
  const s = String(status || '').toLowerCase();
  const color = s === 'resolved' ? '#16a34a' : s === 'pending' ? '#9333ea' : '#6b7280';
  return (
    <span style={{ color, fontSize: 13, fontWeight: 500 }}>
      {status || '—'}
    </span>
  );
}

function SupportHistoryTab() {
  const [ticketSearch, setTicketSearch] = useState('');
  const [ticketPage,   setTicketPage]   = useState(1);
  const PAGE_SIZE = 10;

  // No backend integration yet — render correct structure with empty state
  const tickets = [];

  const filtered = ticketSearch.trim()
    ? tickets.filter(t =>
        (t.id || '').toLowerCase().includes(ticketSearch.toLowerCase()) ||
        (t.subject || '').toLowerCase().includes(ticketSearch.toLowerCase())
      )
    : tickets;

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = filtered.slice((ticketPage - 1) * PAGE_SIZE, ticketPage * PAGE_SIZE);

  const pageBtnStyle = (disabled) => ({
    background: 'none', border: '1px solid #e5e7eb', borderRadius: 6,
    padding: '4px 8px', cursor: disabled ? 'default' : 'pointer',
    color: disabled ? '#d1d5db' : '#374151', fontSize: 14,
  });

  return (
    <div>
      <div style={{ fontSize: 17, fontWeight: 600, color: '#111827', marginBottom: 20 }}>Support Tickets</div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ position: 'relative', width: 320 }}>
          <span style={{
            position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
            color: '#9ca3af', pointerEvents: 'none', display: 'flex',
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/>
              <line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
          </span>
          <input
            type="text"
            placeholder="Search by name or email..."
            value={ticketSearch}
            onChange={e => { setTicketSearch(e.target.value); setTicketPage(1); }}
            style={{
              width: '100%', paddingLeft: 38, paddingRight: 12, height: 37,
              border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14,
              color: '#374151', outline: 'none', boxSizing: 'border-box',
            }}
          />
        </div>
        <button style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '8px 16px', border: '1px solid #e5e7eb', borderRadius: 8,
          background: '#fff', fontSize: 14, color: '#374151', cursor: 'pointer',
        }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="4" y1="6" x2="20" y2="6"/>
            <line x1="8" y1="12" x2="16" y2="12"/>
            <line x1="11" y1="18" x2="13" y2="18"/>
          </svg>
          Filters
        </button>
      </div>

      {paged.length === 0 ? (
        <p style={{ color: '#9ca3af', fontSize: 14 }}>
          No support tickets found. Intercom/Zendesk integration not yet configured.
        </p>
      ) : (
        <div>
          {paged.map((ticket, i) => (
            <div key={i} style={{
              padding: '20px 0',
              borderBottom: i < paged.length - 1 ? '1px solid #f3f4f6' : 'none',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>{ticket.id}</span>
                    <SupportPriorityBadge priority={ticket.priority} />
                  </div>
                  <div style={{ fontSize: 14, color: '#111827', fontWeight: 500, marginBottom: 4 }}>
                    {ticket.subject}
                  </div>
                  <div style={{ fontSize: 12, color: '#9ca3af' }}>{ticket.date}</div>
                </div>
                <SupportStatusBadge status={ticket.status} />
              </div>
            </div>
          ))}

          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '12px 0', marginTop: 8,
          }}>
            <div style={{ fontSize: 14, color: '#6b7280' }}>
              Tickets per page <strong style={{ color: '#374151' }}>50</strong>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, color: '#6b7280' }}>
              <span>Page {ticketPage} of {totalPages}</span>
              <button onClick={() => setTicketPage(1)}             disabled={ticketPage === 1}          style={pageBtnStyle(ticketPage === 1)}>«</button>
              <button onClick={() => setTicketPage(p => p - 1)}    disabled={ticketPage === 1}          style={pageBtnStyle(ticketPage === 1)}>‹</button>
              <button onClick={() => setTicketPage(p => p + 1)}    disabled={ticketPage === totalPages} style={pageBtnStyle(ticketPage === totalPages)}>›</button>
              <button onClick={() => setTicketPage(totalPages)}    disabled={ticketPage === totalPages} style={pageBtnStyle(ticketPage === totalPages)}>»</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FetchEvents({ id, setData }) {
  const eventCategoryOptions = useAPI(`/api/event?group=name&user_id=${id}`);

  useEffect(() => {
    if (eventCategoryOptions.data)
      setData(
        eventCategoryOptions?.data?.results.map(option => ({
          label: eventDisplayName(option.name),
          value: option.name,
        }))
      );
  }, [eventCategoryOptions.data, setData]);

  return false;
}

// ─── Main Component ──────────────────────────────────────────────────────────

// Password complexity — mirrors the server rules in
// controller/userController.js (min 8, ≥1 upper, ≥1 lower).
// Returns an error string, or null when the password passes.
export function passwordComplexityError(password) {
  if (!password) return 'Please enter a password';
  if (password.length < 8) return 'Password must be a minimum of 8 characters';
  if (!/[A-Z]/.test(password)) return 'Password must have at least one upper case letter';
  if (!/[a-z]/.test(password)) return 'Password must have at least one lower case letter';
  return null;
}

// Shared "ghost" pill button — grey border, white fill. Used by the detail
// header actions and the password modal.
const GHOST_BTN = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '8px 16px', border: '1px solid #e5e7eb', borderRadius: 8,
  background: '#fff', fontSize: 14, color: '#374151', cursor: 'pointer',
  fontWeight: 500, whiteSpace: 'nowrap',
};

// "Name · email" line used by the modals — email alone when the user has no
// name, so nameless users never render "null" or a doubled email.
function userSubtitle(user) {
  return user.name ? `${user.name} · ${user.email}` : user.email;
}

// Modal for setting a customer's password. Agent can type one or generate a
// strong one (reveal + copy), then confirm. Writes via PATCH /api/user/:id/password.
export function ChangePasswordModal({ user, onClose }) {
  const [password, setPassword] = useState('');
  const [reveal,   setReveal]   = useState(false);
  const [copied,   setCopied]   = useState(false);
  // one lifecycle: 'editing' → 'submitting' → 'done' | 'error'
  const [status,       setStatus]       = useState('editing');
  const [errorMessage, setErrorMessage] = useState(null);

  const validationError = passwordComplexityError(password);
  const busy = status === 'submitting' || status === 'done';
  const canSubmit = !validationError && !busy;

  function handleGenerate() {
    setPassword(generateStrongPassword());
    setReveal(true);
    setCopied(false);
    setStatus('editing');
    setErrorMessage(null);
  }

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(password);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard unavailable — ignore */ }
  }

  async function handleSubmit() {
    if (!canSubmit) return;
    setStatus('submitting');
    setErrorMessage(null);
    try {
      await Axios.patch(`/api/user/${user.id}/password`, { password });
      setStatus('done');
      setTimeout(onClose, 1200);
    } catch (err) {
      setErrorMessage(err.response?.data?.message || 'Failed to change password');
      setStatus('error');
    }
  }

  const btn = { ...GHOST_BTN, padding: '9px 16px' };

  // status/validation line — single source, no nested ternary
  const line =
    status === 'done'             ? { text: 'Password updated', color: '#16a34a' } :
    status === 'error'            ? { text: errorMessage, color: '#dc2626' } :
    (password && validationError) ? { text: validationError, color: '#dc2626' } :
                                    { text: 'Minimum 8 characters, with upper and lower case.', color: '#9ca3af' };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 12, padding: 24, width: 440, maxWidth: '92vw',
          boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <Icon image='lock' size={16} color='#374151' />
          <h3 style={{ fontSize: 17, fontWeight: 700, color: '#111827', margin: 0 }}>Change password</h3>
        </div>
        <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 18px' }}>
          {userSubtitle(user)}
        </p>

        {/* Password field: input + reveal + copy */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            type={reveal ? 'text' : 'password'}
            value={password}
            autoFocus
            placeholder='New password'
            onChange={e => { setPassword(e.target.value); setErrorMessage(null); if (status === 'error') setStatus('editing'); }}
            onKeyDown={e => { if (e.key === 'Enter') handleSubmit(); }}
            style={{
              flex: 1, fontSize: 14, padding: '9px 12px', borderRadius: 8,
              border: `1px solid ${password && validationError ? '#dc2626' : '#e5e7eb'}`,
              color: '#111827', fontFamily: reveal ? 'ui-monospace, monospace' : 'inherit',
            }}
          />
          <button
            type='button'
            title={reveal ? 'Hide' : 'Reveal'}
            onClick={() => setReveal(r => !r)}
            style={{ ...btn, padding: '9px 10px' }}
          >
            <Icon image={reveal ? 'eye-off' : 'eye'} size={15} color='#6b7280' />
          </button>
          <button
            type='button'
            title='Copy'
            disabled={!password}
            onClick={handleCopy}
            style={{ ...btn, padding: '9px 12px', opacity: password ? 1 : 0.5 }}
          >
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>

        <button
          type='button'
          onClick={handleGenerate}
          style={{
            ...btn, width: '100%', marginTop: 10, display: 'inline-flex',
            alignItems: 'center', justifyContent: 'center', gap: 6,
          }}
        >
          <Icon image='refresh-cw' size={14} color='#6b7280' />
          Generate strong password
        </button>

        {/* validation / status line */}
        <div style={{ minHeight: 18, marginTop: 10, fontSize: 12 }}>
          <span style={{ color: line.color }}>{line.text}</span>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 }}>
          <button type='button' onClick={onClose} style={btn}>Cancel</button>
          <button
            type='button'
            onClick={handleSubmit}
            disabled={!canSubmit}
            style={{
              ...btn, border: '1px solid #2563eb',
              background: canSubmit ? '#2563eb' : '#93c5fd', color: '#fff',
              cursor: canSubmit ? 'pointer' : 'default',
            }}
          >
            {status === 'submitting' ? 'Changing…' : 'Change password'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Client-side mirror of the server's accountId format check — avoids a 500
// from Postgres on a malformed uuid before the lookup even runs.
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Modal for changing a user's default account. Agent enters an account ID,
// looks it up to resolve the name, then confirms before the write goes
// through PATCH /api/user/:id/default-account.
export function ChangeDefaultAccountModal({ user, currentAccountName, onClose, onChanged }) {
  const [accountId, setAccountId] = useState('');
  const [foundAccount, setFoundAccount] = useState(null);
  // one lifecycle: 'editing' → 'looking' → 'confirm' → 'submitting' → 'done' | 'error'
  const [status,       setStatus]       = useState('editing');
  const [errorMessage, setErrorMessage] = useState(null);

  // some users have no name — fall back to email so the copy never reads "null"
  const displayName = user.name || user.email;
  const trimmedId = accountId.trim();
  const idInvalid = trimmedId !== '' && !UUID_REGEX.test(trimmedId);
  const busy = status === 'looking' || status === 'submitting' || status === 'done';
  const canLookup = UUID_REGEX.test(trimmedId) && !busy;

  function resetToEditing() {
    setFoundAccount(null);
    setErrorMessage(null);
    setStatus('editing');
  }

  async function handleLookup() {
    if (!canLookup) return;
    setStatus('looking');
    setErrorMessage(null);
    try {
      const res = await Axios.get(`/api/account/${trimmedId}`);
      const account = res.data?.data?.[0];
      if (!account) {
        setErrorMessage('Account not found');
        setStatus('error');
        return;
      }
      setFoundAccount(account);
      setStatus('confirm');
    } catch (err) {
      setErrorMessage(err.response?.data?.message || 'Account lookup failed');
      setStatus('error');
    }
  }

  async function handleConfirm() {
    if (status !== 'confirm') return;
    setStatus('submitting');
    setErrorMessage(null);
    try {
      await Axios.patch(`/api/user/${user.id}/default-account`, { accountId: trimmedId });
      setStatus('done');
      // refetch only after closing — a refetch clears userData while loading,
      // which would unmount this modal before the success message shows
      setTimeout(() => { onClose(); onChanged?.(); }, 1200);
    } catch (err) {
      setErrorMessage(err.response?.data?.message || 'Failed to change default account');
      setStatus('error');
    }
  }

  const btn = { ...GHOST_BTN, padding: '9px 16px' };
  const confirming = status === 'confirm' || status === 'submitting' || status === 'done';

  // status/validation line — single source, no nested ternary
  const line =
    status === 'done'  ? { text: 'Default account updated', color: '#16a34a' } :
    status === 'error' ? { text: errorMessage, color: '#dc2626' } :
    confirming         ? { text: '', color: '#9ca3af' } :
    idInvalid          ? { text: 'Enter a valid account ID (uuid)', color: '#dc2626' } :
                         { text: 'Paste the account ID, then look it up to confirm.', color: '#9ca3af' };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 12, padding: 24, width: 460, maxWidth: '92vw',
          boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <Icon image='file' size={16} color='#374151' />
          <h3 style={{ fontSize: 17, fontWeight: 700, color: '#111827', margin: 0 }}>Change default account</h3>
        </div>
        <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 18px' }}>
          {userSubtitle(user)}
          {currentAccountName ? ` · current: ${currentAccountName}` : ''}
        </p>

        {confirming && foundAccount ? (
          <p style={{ fontSize: 14, color: '#111827', margin: '0 0 4px' }}>
            Are you sure you want to set <strong>{foundAccount.name}</strong> as the default account for{' '}
            <strong>{displayName}</strong>?
          </p>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type='text'
              value={accountId}
              autoFocus
              placeholder='Account ID'
              onChange={e => { setAccountId(e.target.value); resetToEditing(); }}
              onKeyDown={e => { if (e.key === 'Enter') handleLookup(); }}
              style={{
                flex: 1, fontSize: 14, padding: '9px 12px', borderRadius: 8,
                border: `1px solid ${idInvalid ? '#dc2626' : '#e5e7eb'}`,
                color: '#111827', fontFamily: 'ui-monospace, monospace',
              }}
            />
            <button
              type='button'
              onClick={handleLookup}
              disabled={!canLookup}
              style={{ ...btn, opacity: canLookup ? 1 : 0.5, cursor: canLookup ? 'pointer' : 'default' }}
            >
              {status === 'looking' ? 'Looking…' : 'Look up'}
            </button>
          </div>
        )}

        {/* validation / status line */}
        <div style={{ minHeight: 18, marginTop: 10, fontSize: 12 }}>
          <span style={{ color: line.color }}>{line.text}</span>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 }}>
          {confirming ? (
            <>
              <button type='button' onClick={resetToEditing} disabled={busy && status !== 'confirm'} style={btn}>
                Back
              </button>
              <button
                type='button'
                onClick={handleConfirm}
                disabled={status !== 'confirm'}
                style={{
                  ...btn, border: '1px solid #2563eb',
                  background: status === 'confirm' ? '#2563eb' : '#93c5fd', color: '#fff',
                  cursor: status === 'confirm' ? 'pointer' : 'default',
                }}
              >
                {status === 'submitting' ? 'Saving…' : 'Set default account'}
              </button>
            </>
          ) : (
            <button type='button' onClick={onClose} style={btn}>Cancel</button>
          )}
        </div>
      </div>
    </div>
  );
}

export function UserDetail() {
  const limit = 25;
  const { id, tab: tabParam } = useParams();
  const navigate = useNavigate();
  const viewContext = useContext(ViewContext);

  // redirect /users/:id → /users/:id/profile (also canonicalise unknown slugs)
  useEffect(() => {
    if (!tabParam || !SLUG_TO_TAB[tabParam]) navigate(`/users/${id}/profile`, { replace: true });
  }, [id, tabParam, navigate]);

  const activeTab = SLUG_TO_TAB[tabParam] || 'Profile';
  const [eventName,         setEventName]         = useState('prompt_submitted');
  const [offset,            setOffset]            = useState(0);
  const [total,             setTotal]             = useState(0);
  const [eventCategories,   setEventCategories]   = useState([]);
  const [chart,             setChart]             = useState(false);
  const [eventSearch,       setEventSearch]       = useState('');
  const [verified,          setVerified]          = useState(null);
  const [verifiedStatus,    setVerifiedStatus]    = useState(null); // 'success' | 'error'
  const [dateRange,         setDateRange]         = useState(() => {
    const range = getDateRange('last_7');
    return { ...range, preset: 'last_7' };
  });

  const dateParams = `&start_date=${dateRange.start}&end_date=${dateRange.end}`;
  const user         = useAPI(`/api/user/${id}`);
  const events       = useAPI(`/api/event?&offset=${offset}&name=${eventName}&user_id=${id}${dateParams}`);
  const loginHistory = useAPI(`/api/login?user_id=${id}`);
  const billing      = useAPI(`/api/user/${id}/billing`);

  useEffect(() => {
    if (events.data?.list) setTotal(events.data.list.total);
    if (events.data?.chart) setChart(events.data.chart);
  }, [events.data]);

  const userData    = user.data?.[0];
  const account     = useAPI(userData?.default_account ? `/api/account/${userData.default_account}` : null);
  const defaultAccountId = userData?.default_account;
  const creditBalance     = useAPI(defaultAccountId ? `/api/credit/account/${defaultAccountId}/user/${id}/balance` : null);
  const creditAccountPool = useAPI(defaultAccountId ? `/api/credit/account/${defaultAccountId}/balance` : null);
  const creditFeatureUsage = useAPI(defaultAccountId ? `/api/credit/account/${defaultAccountId}/user/${id}/feature-usage` : null);
  const creditHistory     = useAPI(defaultAccountId ? `/api/credit/account/${defaultAccountId}/ledger?userId=${id}&limit=20` : null);
  const creditBundles     = useAPI(defaultAccountId ? `/api/credit/account/${defaultAccountId}/bundles` : null);
  const creditUsersForAccount = useAPI(defaultAccountId ? `/api/credit/account/${defaultAccountId}/users` : null);
  // Only the total is read (see promptCount below) — model/event.js computes
  // that separately from the row limit, so `limit=1` is enough. Scoped to the
  // same current-cycle window as the rest of this panel once it's known, so
  // this count doesn't disagree with the "consumed this cycle" figures.
  const featureUsageCycleStart = creditFeatureUsage.data?.cycleStart;
  const featureUsageCycleEnd = creditFeatureUsage.data?.cycleEnd;
  const promptParams = new URLSearchParams({ name: 'prompt_submitted', user_id: id, limit: '1' });
  if (featureUsageCycleStart) promptParams.set('start_date', formatDate(new Date(featureUsageCycleStart)));
  if (featureUsageCycleEnd) promptParams.set('end_date', formatDate(new Date(featureUsageCycleEnd)));
  const creditPrompts     = useAPI(`/api/event?${promptParams.toString()}`);
  const licenses    = parseLicenses(userData?.assigned_licenses);
  const dateCreated = convertToLocalTime(userData?.date_created);
  const lastActive  = convertToLocalTime(userData?.last_active);

  useEffect(() => {
    if (userData?.verified != null) setVerified(userData.verified);
  }, [userData?.verified]);

  async function handleVerifiedChange(e) {
    const newVal = Number(e.target.value);
    const prevVal = verified;
    setVerified(newVal);
    setVerifiedStatus(null);
    try {
      await Axios.patch(`/api/user/${userData.id}`, { verified: newVal });
      setVerifiedStatus('success');
    } catch {
      setVerified(prevVal);
      setVerifiedStatus('error');
    }
    setTimeout(() => setVerifiedStatus(null), 2000);
  }

  function handleDateRangeChange(range) {
    setDateRange(range);
    setOffset(0);
  }

  const [impersonating, setImpersonating] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showDefaultAccountModal, setShowDefaultAccountModal] = useState(false);

  async function handleImpersonate() {
    if (impersonating) return;
    setImpersonating(true);
    try {
      const res = await Axios.post(`/api/user/impersonate/${id}`);
      const token = res.data?.data?.token;
      if (!token) throw new Error('No token returned');
      const url = `${Settings[process.env.NODE_ENV].remote_client}/signin/impersonate?token=${token}`;
      window.open(url, '_blank');
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Impersonation failed');
    } finally {
      setImpersonating(false);
    }
  }

  // Rough prompt volume from chart datasets
  const promptVolume = chart
    ? (chart.datasets?.[0]?.data?.reduce((a, b) => a + b, 0) || 0)
    : 0;

  return (
    <Animate>
      <FetchEvents key={id} id={id} setData={setEventCategories} />
      <Breadcrumbs items={[{ name: 'users', url: '/users' }, { name: 'detail', url: `/users/${id}` }]} />

      <Card loading={user.loading}>

        {/* ── Page Header ── */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <h2 style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: 0 }}>
              Users / {userData?.name || '…'}
            </h2>
            <p style={{ fontSize: 14, color: '#6b7280', margin: '4px 0 0' }}>
              Manage and monitor all users
            </p>
          </div>
          {userData && (
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={() => setShowPasswordModal(true)}
                style={GHOST_BTN}
                onMouseEnter={e => { e.currentTarget.style.background = '#f9fafb'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}
              >
                <Icon image='lock' size={14} color='#6b7280' />
                Change password
              </button>
              <button
                onClick={handleImpersonate}
                disabled={impersonating}
                style={{
                  ...GHOST_BTN,
                  background: impersonating ? '#f3f4f6' : '#fff',
                  cursor: impersonating ? 'default' : 'pointer',
                }}
                onMouseEnter={e => { if (!impersonating) e.currentTarget.style.background = '#f9fafb'; }}
                onMouseLeave={e => { if (!impersonating) e.currentTarget.style.background = '#fff'; }}
              >
                <Icon image='log-in' size={14} color='#6b7280' />
                {impersonating ? 'Signing in…' : 'Impersonate'}
              </button>
            </div>
          )}

          {showPasswordModal && userData && (
            <ChangePasswordModal
              user={userData}
              onClose={() => setShowPasswordModal(false)}
            />
          )}

          {showDefaultAccountModal && userData && (
            <ChangeDefaultAccountModal
              user={userData}
              currentAccountName={account.data?.[0]?.name}
              onClose={() => setShowDefaultAccountModal(false)}
              onChanged={user.refetch}
            />
          )}
        </div>

        {/* ── Tabs ── */}
        <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', marginBottom: 28, gap: 0 }}>
          {TABS.map(tab => (
            <button
              key={tab}
              onClick={() => navigate(`/users/${id}/${TAB_SLUGS[tab]}`)}
              style={{
                padding: '10px 16px',
                border: 'none',
                borderBottom: activeTab === tab ? '2px solid #2563eb' : '2px solid transparent',
                background: 'none',
                cursor: 'pointer',
                color: activeTab === tab ? '#2563eb' : '#6b7280',
                fontWeight: activeTab === tab ? 600 : 400,
                fontSize: 14,
                whiteSpace: 'nowrap',
                marginBottom: -1,
              }}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* ── Profile Tab ── */}
        {activeTab === 'Profile' && userData && (
          <div style={{ border: '1px solid #f3f4f6', borderRadius: 8, overflow: 'hidden' }}>

            <InfoRow>
              <InfoField icon='list'   label='ID'>{userData.id}</InfoField>
              <InfoField icon='user'   label='Name'>{userData.name}</InfoField>
              <InfoField icon='mail'   label='Email'>{userData.email}</InfoField>
            </InfoRow>

            <InfoRow>
              <InfoField icon='file' label='Default Account'>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  {userData.default_account ? (
                    <a
                      href={`/accounts/${userData.default_account}`}
                      onClick={e => { e.preventDefault(); if (e.ctrlKey || e.metaKey) { window.open(`/accounts/${userData.default_account}`, '_blank'); } else { navigate(`/accounts/${userData.default_account}`); } }}
                      style={{ color: '#2563eb', textDecoration: 'none' }}
                    >
                      {account.data?.[0]?.name || userData.default_account}
                    </a>
                  ) : '—'}
                  <button
                    type='button'
                    title='Change default account'
                    onClick={() => setShowDefaultAccountModal(true)}
                    style={{ border: 'none', background: 'none', padding: 2, cursor: 'pointer', display: 'inline-flex' }}
                  >
                    <Icon image='edit-2' size={13} color='#6b7280' />
                  </button>
                </span>
              </InfoField>
              <DateTimeField icon='calendar' label='Date Created' value={dateCreated} />
              <DateTimeField icon='clock'    label='Last Active'  value={lastActive} />
            </InfoRow>

            {/* Row 3: Verified + Licenses (1/3 + 2/3) */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', borderBottom: '1px solid #f3f4f6' }}>
              <InfoField icon='check-circle' label='Verified'>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <select
                    value={verified ? '1' : '0'}
                    onChange={handleVerifiedChange}
                    style={{ fontSize: 14, border: '1px solid #e5e7eb', borderRadius: 4, padding: '2px 6px', background: '#fff', color: '#374151', cursor: 'pointer' }}
                  >
                    <option value="1">True</option>
                    <option value="0">False</option>
                  </select>
                  {verifiedStatus === 'success' && <span style={{ fontSize: 12, color: '#16a34a' }}>Saved</span>}
                  {verifiedStatus === 'error' && <span style={{ fontSize: 12, color: '#dc2626' }}>Failed</span>}
                </span>
              </InfoField>
              <div style={{ padding: '16px 16px 20px', borderLeft: '1px solid #f3f4f6' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                  <Icon image='credit-card' size={14} color='#9ca3af' />
                  <span style={{ fontSize: 13, color: '#6b7280' }}>Licenses</span>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {licenses.length > 0 ? (
                    <>
                      {licenses.slice(0, 3).map((lic, i) => (
                        <span key={i} style={{
                          display: 'inline-block', padding: '3px 10px',
                          borderRadius: 12, fontSize: 12, fontWeight: 600,
                          background: i === 0 ? '#eff6ff' : '#f9fafb',
                          color: i === 0 ? '#2563eb' : '#374151',
                          border: `1px solid ${i === 0 ? '#bfdbfe' : '#e5e7eb'}`,
                        }}>
                          {lic}
                        </span>
                      ))}
                      {licenses.length > 3 && (
                        <span style={{
                          display: 'inline-block', padding: '3px 8px',
                          borderRadius: 12, fontSize: 12, border: '1px solid #e5e7eb', color: '#6b7280',
                        }}>
                          +{licenses.length - 3}
                        </span>
                      )}
                    </>
                  ) : (
                    <span style={{ color: '#9ca3af', fontSize: 14 }}>—</span>
                  )}
                </div>
              </div>
            </div>

            {/* Row 4: Mobile, Position, Region, Industry Bodies */}
            <InfoRow cols={4}>
              <InfoField icon='phone' label='Mobile'>
                {userData.metadata?.contact_number || '—'}
              </InfoField>
              <InfoField icon='briefcase' label='Position'>
                {userData.metadata?.role
                  ? (userData.metadata.role === 'Other'
                    ? (userData.metadata.specified_role || 'Other')
                    : userData.metadata.role)
                  : '—'}
              </InfoField>
              <InfoField icon='map-pin' label='Region'>
                {account.data?.[0]?.region || '—'}
              </InfoField>
              <InfoField icon='award' label='Industry Bodies'>
                {(() => {
                  const bodies = account.data?.[0]?.metadata?.professional_bodies;
                  if (!bodies || !Array.isArray(bodies) || bodies.length === 0) return '—';
                  return bodies.join(', ');
                })()}
              </InfoField>
            </InfoRow>

            <div style={{
              display: 'grid', gridTemplateColumns: '1fr 1fr',
              gap: 48, padding: '24px 16px', borderTop: '1px solid #f3f4f6',
            }}>
              <AccountMeta account={account} />
              <FeatureAdoption userId={id} />
            </div>

            <div style={{ padding: '24px 16px', borderTop: '1px solid #f3f4f6' }}>
              <PricingMigration userId={id} />
            </div>

          </div>
        )}

        {/* ── Usage Tab ── */}
        {activeTab === 'Usage' && (
          <div>
            {/* Event selector */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: '#111827', marginBottom: 12 }}>Select Event</div>
              <div style={{ maxWidth: 400 }}>
                <Select
                  type='select'
                  options={eventCategories}
                  value={eventName}
                  onChange={(_, val) => { setEventName(val); setOffset(0); }}
                />
              </div>
            </div>

            {/* Stat cards — shown when chart data is available */}
            {chart && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 24 }}>
                {[
                  {
                    label: 'Prompt Volume',
                    value: promptVolume.toLocaleString(),
                    iconBg: '#eff6ff',
                    iconColor: '#2563eb',
                    icon: (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                      </svg>
                    ),
                  },
                  {
                    label: 'API Calls',
                    value: total?.toLocaleString() || '0',
                    iconBg: '#fdf4ff',
                    iconColor: '#9333ea',
                    icon: (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
                      </svg>
                    ),
                  },
                  {
                    label: 'Active Users',
                    value: '1',
                    iconBg: '#f0fdf4',
                    iconColor: '#16a34a',
                    icon: (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                        <circle cx="9" cy="7" r="4"/>
                        <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                        <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                      </svg>
                    ),
                  },
                ].map(card => (
                  <div key={card.label} style={{
                    background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10,
                    padding: '18px 20px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
                  }}>
                    <div>
                      <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 8 }}>{card.label}</div>
                      <div style={{ fontSize: 26, fontWeight: 700, color: '#111827', lineHeight: 1 }}>{card.value}</div>
                    </div>
                    <div style={{
                      width: 36, height: 36, borderRadius: 8,
                      background: card.iconBg, color: card.iconColor,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                      {card.icon}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {chart && (
              <div style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <div style={{ fontWeight: 600, fontSize: 15, color: '#111827' }}>Prompts</div>
                  <DateRangeSelector value={dateRange} onChange={handleDateRangeChange} />
                </div>
                <Chart type='line' data={chart} />
              </div>
            )}

            {/* Events table with search + filter bar */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ position: 'relative', width: 320 }}>
                <span style={{
                  position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
                  color: '#9ca3af', pointerEvents: 'none', display: 'flex',
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8"/>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                </span>
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={eventSearch}
                  onChange={e => setEventSearch(e.target.value)}
                  style={{
                    width: '100%', paddingLeft: 38, paddingRight: 12, height: 37,
                    border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14,
                    color: '#374151', outline: 'none', boxSizing: 'border-box',
                  }}
                />
              </div>
              <button style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '8px 16px', border: '1px solid #e5e7eb', borderRadius: 8,
                background: '#fff', fontSize: 14, color: '#374151', cursor: 'pointer',
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="4" y1="6" x2="20" y2="6"/>
                  <line x1="8" y1="12" x2="16" y2="12"/>
                  <line x1="11" y1="18" x2="13" y2="18"/>
                </svg>
                Filters
              </button>
            </div>

            <Paginate
              offset={offset} limit={limit} total={total}
              loading={events.loading} onChange={setOffset}
            />
            <Table
              selectable
              loading={events.loading}
              data={events?.data?.list?.results
                ?.filter(event => eventMatchesSearch(event, eventSearch))
                .map(event => ({
                  ...event,
                  name: eventDisplayName(event.name),
                  time: convertToLocalTime(event.time),
                }))}
              show={['name', 'time', 'user_email']}
            />
          </div>
        )}

        {/* ── Credits Tab ── */}
        {activeTab === 'Credits' && (() => {
          const LEDGER_ENTRY_TYPE_LABEL = {
            grant: 'Grant', spend: 'Spend', expire: 'Expire', refund: 'Refund',
            adjustment: 'Adjustment', void: 'Void', task_refused: 'Task refused',
          };
          const TIER_BADGE_COLOR = { elite: 'blue', adviser: 'green', starter: 'orange' };

          const bal = creditBalance.data;
          const tier = bal?.tier;
          const personalGranted = bal?.personalGranted ?? 0;
          const personalRemaining = bal?.personalRemaining ?? 0;
          const isAccountPoolEnabled = Boolean(bal?.isAccountPoolEnabled);
          const periodEnd = bal?.periodEnd;
          const pct = personalGranted > 0 ? Math.max(0, Math.min(100, Math.round((personalRemaining / personalGranted) * 100))) : 0;

          const workspacePoolBalance = (creditAccountPool.data?.accountRemaining ?? 0) - (creditAccountPool.data?.accountDeficit ?? 0);

          const usage = creditFeatureUsage.data;
          const deepResearchCredits = usage?.deepResearchCredits ?? 0;
          const deepResearchRuns = usage?.deepResearchRuns ?? 0;
          const meetingNotesCredits = usage?.meetingNotesCredits ?? 0;
          const meetingNotesHours = usage?.meetingNotesHours ?? 0;
          const spendThisCycle = deepResearchCredits + meetingNotesCredits;
          const promptCount = creditPrompts.data?.list?.total ?? 0;

          const historyEntries = creditHistory.data?.entries || [];

          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Header strip */}
              <div style={{
                display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 12,
                background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 20px',
                fontSize: 13, color: '#6b7280',
              }}>
                {tier && <Badge text={tier} color={TIER_BADGE_COLOR[tier] || 'blue'} />}
                {tier === 'starter' ? (
                  <span>Starter plan has no allowance</span>
                ) : (
                  <span><span style={{ fontWeight: 600, color: '#111827' }}>{personalGranted.toLocaleString()}</span> credits/mo</span>
                )}
                <span><span style={{ fontWeight: 600, color: personalRemaining <= 0 ? '#dc2626' : '#111827' }}>{personalRemaining.toLocaleString()}</span> left</span>
                <span>pool access <span style={{ fontWeight: 600, color: '#111827' }}>{isAccountPoolEnabled ? 'on' : 'off'}</span></span>
                <button
                  onClick={() => viewContext.modal.show({
                    title: 'Add Credits',
                    wide: true,
                    content: (
                      <AddCreditsModal
                        accountId={defaultAccountId}
                        accountName={account.data?.[0]?.name}
                        accountDeficit={creditAccountPool.data?.accountDeficit ?? 0}
                        activeLots={(creditBundles.data || []).filter(l => l.status === 'active' && l.remaining > 0)}
                        users={creditUsersForAccount.data || []}
                        defaultPool="personal"
                        defaultUserId={id}
                      />
                    ),
                  }, () => {
                    creditBalance.refetch();
                    creditAccountPool.refetch();
                    creditBundles.refetch();
                    creditUsersForAccount.refetch();
                    creditHistory.refetch();
                  })}
                  style={{
                    marginLeft: 'auto', padding: '4px 12px', fontSize: 12, fontWeight: 600, borderRadius: 6,
                    border: '1px solid #1974f9', background: '#1974f9', color: '#fff', cursor: 'pointer',
                  }}
                >
                  Add Credits
                </button>
              </div>

              {/* Stat cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
                <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '18px 20px' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Personal Allowance</div>
                  {tier === 'starter' ? (
                    <div style={{ fontSize: 14, color: '#9ca3af' }}>No allowance</div>
                  ) : (
                    <>
                      <div style={{ fontSize: 14, color: '#111827' }}>
                        <span style={{ fontSize: 18, fontWeight: 700 }}>{personalRemaining.toLocaleString()}</span>
                        <span style={{ color: '#9ca3af', fontSize: 12, marginLeft: 4 }}>
                          of {personalGranted.toLocaleString()}{periodEnd ? ` · refills ${new Date(periodEnd).toISOString().slice(0, 10)}` : ''}
                        </span>
                      </div>
                      <div style={{ height: 6, borderRadius: 999, background: '#f3f4f6', overflow: 'hidden', marginTop: 10 }}>
                        <div style={{ height: 6, background: '#1974f9', width: `${pct}%` }} />
                      </div>
                    </>
                  )}
                </div>
                <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '18px 20px' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Workspace Pool Access</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: isAccountPoolEnabled ? '#16a34a' : '#dc2626' }}>{isAccountPoolEnabled ? 'On' : 'Off'}</div>
                </div>
                <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '18px 20px' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Spend This Cycle</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#111827' }}>{spendThisCycle.toLocaleString()} <span style={{ fontSize: 12, fontWeight: 400, color: '#9ca3af' }}>credits</span></div>
                </div>
              </div>

              {/* Workspace credit access + Credit feature usage */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '20px 24px' }}>
                  <div style={{ fontSize: 15, fontWeight: 600, color: '#111827', marginBottom: 16 }}>Workspace credit access</div>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12, fontSize: 14 }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', marginRight: 10, background: isAccountPoolEnabled ? '#16a34a' : '#dc2626' }} />
                    <span style={{ color: '#374151', flex: 1 }}>Workspace pool access</span>
                    <span style={{ color: '#111827', fontWeight: 500 }}>{isAccountPoolEnabled ? 'true' : 'false — set by admin'}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', fontSize: 14 }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', marginRight: 10, background: workspacePoolBalance > 0 ? '#16a34a' : '#dc2626' }} />
                    <span style={{ color: '#374151', flex: 1 }}>Workspace pool balance</span>
                    <span style={{ color: '#111827', fontWeight: 500 }}>{workspacePoolBalance.toLocaleString()} credits</span>
                  </div>
                </div>
                <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '20px 24px' }}>
                  <div style={{ fontSize: 15, fontWeight: 600, color: '#111827', marginBottom: 16 }}>Credit Feature Usage</div>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10, fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    <div style={{ width: 18, flex: 'none' }} />
                    <span style={{ flex: 1 }}>Feature</span>
                    <span style={{ width: 90, textAlign: 'right' }}>Credits</span>
                    <span style={{ width: 150, textAlign: 'right' }}>Volume</span>
                  </div>
                  {[
                    { label: 'Deep research', credits: deepResearchCredits, volume: deepResearchCredits === 0 ? 'no runs' : `${deepResearchRuns} runs`, dot: deepResearchCredits === 0 ? '#9ca3af' : '#1974f9' },
                    { label: 'AI meeting notes', credits: meetingNotesCredits, volume: meetingNotesCredits === 0 ? 'no sessions' : `${meetingNotesHours} hrs`, dot: meetingNotesCredits === 0 ? '#9ca3af' : '#f97316' },
                    { label: 'Chat and search (free)', credits: '—', volume: `${promptCount.toLocaleString()} prompts`, dot: '#9ca3af' },
                  ].map((row) => (
                    <div key={row.label} style={{ display: 'flex', alignItems: 'center', padding: '9px 0', fontSize: 14, borderTop: '1px solid #f3f4f6' }}>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', marginRight: 10, flexShrink: 0, background: row.dot }} />
                      <span style={{ color: '#374151', flex: 1 }}>{row.label}</span>
                      <span style={{ color: '#111827', fontWeight: 500, width: 90, textAlign: 'right' }}>{typeof row.credits === 'number' ? row.credits.toLocaleString() : row.credits}</span>
                      <span style={{ color: '#374151', width: 150, textAlign: 'right' }}>{row.volume}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Credit history */}
              <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: '1px solid #f3f4f6' }}>
                  <span style={{ fontSize: 15, fontWeight: 600, color: '#111827' }}>Credit history</span>
                  {defaultAccountId && (
                    <button
                      onClick={() => navigate(`/accounts/${defaultAccountId}/credits`)}
                      style={{ fontSize: 12, color: '#1974f9', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                    >
                      Full account ledger
                    </button>
                  )}
                </div>
                {historyEntries.length === 0 ? (
                  <div style={{ padding: '32px 16px', textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>No credit activity yet</div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        {['Timestamp', 'Task', 'Drawn From', 'Change'].map((h) => (
                          <th key={h} style={{ textAlign: 'left', padding: '10px 16px', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {historyEntries.map((entry) => (
                        <tr key={entry.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                          <td style={{ padding: '12px 16px', fontSize: 13 }}>{convertToLocalTime(entry.createdAt)}</td>
                          <td style={{ padding: '12px 16px', fontSize: 13, color: '#111827', fontWeight: 500 }}>
                            <div>{LEDGER_ENTRY_TYPE_LABEL[entry.entryType] || entry.entryType}</div>
                            <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2, fontWeight: 400 }}>{entry.eventLabel}</div>
                          </td>
                          <td style={{ padding: '12px 16px', fontSize: 13, color: '#374151' }}>{entry.drawnFrom || <span style={{ color: '#9ca3af' }}>—</span>}</td>
                          <td style={{ padding: '12px 16px', fontSize: 13, fontWeight: 600, color: entry.amount == null ? '#9ca3af' : entry.amount < 0 ? '#dc2626' : '#16a34a' }}>
                            {entry.amount == null ? '—' : `${entry.amount > 0 ? '+' : ''}${entry.amount.toLocaleString()}`}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          );
        })()}

        {/* ── Activity Timeline Tab ── */}
        {activeTab === 'Activity Timeline' && (
          <ActivityTimeline loginHistory={loginHistory} />
        )}

        {/* ── Billing and Subscription Tab ── */}
        {activeTab === 'Billing and Subscription' && (
          <BillingTab billing={billing} />
        )}

        {/* ── Support History Tab ── */}
        {activeTab === 'Support History' && (
          <SupportHistoryTab />
        )}

      </Card>
    </Animate>
  );
}
